import { useMeilisearch } from '~/composables/useMeilisearch';

interface UseSearchDropdownOptions {
    model: string;
    hitsPerPage?: number;
    debounceMs?: number;
    minLength?: number;
}

export function useSearchDropdown<T>(options: UseSearchDropdownOptions) {
    const { search: searchMeilisearch } = useMeilisearch();
    const results = ref<T[]>([]);
    const showDropdown = ref(false);
    const loading = ref(false);
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    onScopeDispose(() => {
        if (searchTimeout) clearTimeout(searchTimeout);
    });

    const search = (query: string) => {
        if (searchTimeout) clearTimeout(searchTimeout);
        if (query.length < (options.minLength ?? 2)) {
            results.value = [];
            showDropdown.value = false;
            return;
        }
        searchTimeout = setTimeout(async () => {
            loading.value = true;
            try {
                const response = await searchMeilisearch({
                    model: options.model,
                    query,
                    hitsPerPage: options.hitsPerPage ?? 5,
                });
                results.value = response.hits;
                showDropdown.value = response.hits.length > 0;
            } catch {
                results.value = [];
                showDropdown.value = false;
            } finally {
                loading.value = false;
            }
        }, options.debounceMs ?? 150);
    };

    const hideDropdown = () => setTimeout(() => (showDropdown.value = false), 200);

    const selectItem = (item: T) => {
        showDropdown.value = false;
        results.value = [];
        return item;
    };

    const clear = () => {
        if (searchTimeout) clearTimeout(searchTimeout);
        results.value = [];
        showDropdown.value = false;
    };

    return { results, showDropdown, loading, search, hideDropdown, selectItem, clear };
}
